import React, { createContext, useRef, useState, useCallback, Children, useEffect, useContext, useMemo, cloneElement } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { registerDefaultsForModule, withStyles, createUseStyles, useTheme } from '@glu/theming';
import locale from '@glu/locale';

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);

    if (enumerableOnly) {
      symbols = symbols.filter(function (sym) {
        return Object.getOwnPropertyDescriptor(object, sym).enumerable;
      });
    }

    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];

  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;

  var _s, _e;

  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];

  return arr2;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

/* istanbul ignore file */
var legacy = (function (_ref) {
  var palette = _ref.palette,
      typography = _ref.typography;
  return {
    indicator: {
      backgroundColor: palette.primary.normal,
      height: 4,
      transitionDuration: 300,
      transition: 'cubic-bezier(0.4, 0, 0.2, 1)'
    },
    tab: {
      color: palette.text.dark,
      font: 'inherit',
      selectedColor: palette.primary.normal,
      focusColor: palette.focus.normal
    },
    tabAddition: {
      textTransform: 'none',
      font: typography.fontFamily,
      fontSize: 16,
      fontWeight: typography.fontWeightHeavy
    },
    tabDark: {
      color: palette.text.light,
      darkFocusColor: palette.focus.dark
    },
    wrapBorder: {
      borderBottom: "1px solid ".concat(palette.grey.G300)
    },
    darkFocusColor: palette.focus.dark,
    focusColor: palette.focus.normal,
    styledTab: {
      backgroundColor: palette.grey.G100,
      fontFamily: "\"Roboto Condensed\", ".concat(typography.fontFamily),
      borderBottomColor: "".concat(palette.grey.G400, "!important")
    },
    defaultTabBorderColor: palette.grey.G400,
    defaultTabBorder: "solid ".concat(palette.grey.G400),
    badge: {
      backgroundColor: palette.grey.G200,
      color: palette.text.dark,
      padding: 5,
      minWidth: 25,
      borderRadius: 20,
      display: 'block',
      fontSize: 12,
      fontWeight: 'bold'
    }
  };
});

/* istanbul ignore file */
var ash = (function (_ref) {
  var palette = _ref.palette,
      typography = _ref.typography;
  return {
    indicator: {
      backgroundColor: palette.colors.lime,
      height: 4,
      transitionDuration: 300,
      transition: 'cubic-bezier(0.4, 0, 0.2, 1)'
    },
    tab: {
      color: palette.primary.normal,
      selectedColor: palette.text.dark,
      focusColor: palette.focus.normal
    },
    tabAddition: {
      font: "20px \"Roboto Condensed\", ".concat(typography.fontFamily),
      textTransform: 'uppercase'
    },
    tabDark: {
      color: palette.text.light,
      darkFocusColor: palette.focus.dark
    },
    wrapBorder: {
      paddingTop: 10
    },
    styledTab: {
      backgroundColor: palette.colors.gallery,
      fontFamily: "".concat(typography.fontFamily),
      borderBottomColor: "".concat(palette.grey.G400, "!important")
    },
    defaultTabBorderColor: palette.grey.G400,
    defaultTabBorder: "solid ".concat(palette.grey.G400),
    badge: {
      backgroundColor: palette.colors.catskillWhite,
      color: palette.text.dark,
      padding: 5,
      minWidth: 25,
      borderRadius: 20,
      display: 'block',
      fontSize: 12,
      fontWeight: 'bold'
    }
  };
});

var themes = {
  ash: ash,
  legacy: legacy
};
registerDefaultsForModule('tabs', function (theme) {
  return themes[theme.name](theme);
});

var styles = (function (_ref) {
  var wrapBorder = _ref.tabs.wrapBorder,
      palette = _ref.palette;
  return {
    root: {
      overflowX: 'auto',
      marginBottom: 24
    },
    tabsWrapper: _objectSpread2({
      position: 'relative',
      '& .tab': {
        marginRight: 30,
        marginBottom: 3
      },
      '&.vertical': {
        width: '200px',
        '&.right': {
          borderRight: "1px solid ".concat(palette.grey.G400),
          margin: '0 0 0 auto'
        },
        '&.left': {
          borderLeft: "1px solid ".concat(palette.grey.G400),
          margin: '0 auto 0 0'
        },
        '& .tab': {
          height: '40px',
          margin: '0 0 8px 0',
          padding: function padding(_ref2) {
            var left = _ref2.left;
            return left ? '0 0 0 16px' : '0 16px 0 0';
          },
          textAlign: function textAlign(_ref3) {
            var left = _ref3.left;
            return left ? 'left' : 'right';
          },
          '&:hover': {
            backgroundColor: '#ededed'
          }
        }
      },
      whiteSpace: 'nowrap'
    }, wrapBorder),
    tabs: {
      display: 'flex',
      flexDirection: function flexDirection(_ref4) {
        var vertical = _ref4.vertical;
        return vertical ? 'column' : 'row';
      }
    }
  };
});

var styles$1 = (function (_ref) {
  var _indicator = _ref.tabs.indicator;
  return {
    indicator: function indicator(_ref2) {
      var vertical = _ref2.vertical,
          left = _ref2.left;
      return _objectSpread2({
        position: 'absolute',
        transition: "all ".concat(_indicator.transitionDuration, "ms ").concat(_indicator.transition),
        backgroundColor: _indicator.backgroundColor
      }, vertical ? _defineProperty({
        width: _indicator.height
      }, left ? 'left' : 'right', 0) : {
        height: _indicator.height,
        bottom: 0
      });
    }
  };
});

var _excluded = ["classes", "className", "vertical", "left"];
function TabIndicatorComponent(_ref) {
  var classes = _ref.classes,
      className = _ref.className,
      vertical = _ref.vertical,
      left = _ref.left,
      other = _objectWithoutProperties(_ref, _excluded);

  return /*#__PURE__*/React.createElement("span", _extends({
    className: classnames(classes.indicator, className)
  }, other));
}
TabIndicatorComponent.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  className: PropTypes.string,
  vertical: PropTypes.bool,
  left: PropTypes.bool
};
TabIndicatorComponent.defaultProps = {
  className: undefined,
  vertical: false,
  left: false
};
var TabIndicator = withStyles(styles$1)(TabIndicatorComponent);

var TabsContext = /*#__PURE__*/createContext('');

/* istanbul ignore next */

var TabsComponent = function TabsComponent(props) {
  var _props$classes = props.classes,
      rootClass = _props$classes.root,
      tabs = _props$classes.tabs,
      tabsWrapper = _props$classes.tabsWrapper,
      vertical = props.vertical,
      ariaLabel = props.ariaLabel,
      left = props.left,
      children = props.children,
      className = props.className;
  var wrapperRef = useRef(null);
  var tabsRef = useRef(null);

  var _useState = useState(),
      _useState2 = _slicedToArray(_useState, 2),
      indicatorStyle = _useState2[0],
      setIndicatorStyle = _useState2[1];

  var _useState3 = useState(props.value),
      _useState4 = _slicedToArray(_useState3, 2),
      value = _useState4[0],
      setValue = _useState4[1];

  var getTabDimensions = function getTabDimensions(selectedTabIndex) {
    if (!tabsRef.current) {
      return {
        tabLeft: 0,
        tabWidth: 0,
        tabTop: 0,
        tabHeight: 0,
        wrapperLeft: 0,
        wrapperTop: 0
      };
    }

    var _tabsRef$current$chil = tabsRef.current.children[selectedTabIndex].getBoundingClientRect(),
        tabLeft = _tabsRef$current$chil.left,
        tabWidth = _tabsRef$current$chil.width,
        tabTop = _tabsRef$current$chil.top,
        tabHeight = _tabsRef$current$chil.height;

    var _wrapperRef$current$g = wrapperRef.current.getBoundingClientRect(),
        wrapperLeft = _wrapperRef$current$g.left,
        wrapperTop = _wrapperRef$current$g.top;

    return {
      tabLeft: tabLeft,
      tabWidth: tabWidth,
      tabTop: tabTop,
      tabHeight: tabHeight,
      wrapperLeft: wrapperLeft,
      wrapperTop: wrapperTop
    };
  };

  var getIndicatorStyle = function getIndicatorStyle(_ref) {
    var tabLeft = _ref.tabLeft,
        tabWidth = _ref.tabWidth,
        tabTop = _ref.tabTop,
        tabHeight = _ref.tabHeight,
        wrapperLeft = _ref.wrapperLeft,
        wrapperTop = _ref.wrapperTop;
    return vertical ? {
      top: Math.round(tabTop - wrapperTop),
      height: Math.round(tabHeight)
    } : {
      left: Math.round(tabLeft - wrapperLeft),
      width: Math.round(tabWidth)
    };
  };

  var updateIndicatorState = function updateIndicatorState(childrenLocal) {
    var selectedTabIndex = childrenLocal.findIndex(function (child) {
      return value === child.props.value;
    });

    if (selectedTabIndex < 0) {
      return;
    }

    var style = getIndicatorStyle(getTabDimensions(selectedTabIndex));

    if (JSON.stringify(style) !== JSON.stringify(indicatorStyle)) {
      setIndicatorStyle(style);
    }
  };

  var checkAndUpdate = useCallback(function () {
    var parsedChildren = Children.toArray(props.children);

    if (parsedChildren.length > 0) {
      updateIndicatorState(parsedChildren);
    }
  });
  useEffect(function () {
    if (window.document.fonts) {
      window.document.fonts.ready.then(checkAndUpdate);
    } else {
      checkAndUpdate();
    }

    if (props.value !== value && props.value !== 0) {
      setValue(props.value);
    }
  }, [props.value, value, checkAndUpdate]);
  return /*#__PURE__*/React.createElement(TabsContext.Provider, {
    value: {
      value: value,
      setValue: setValue
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: rootClass
  }, /*#__PURE__*/React.createElement("nav", {
    "aria-label": ariaLabel || locale.get('tabs'),
    className: classnames(tabsWrapper, {
      vertical: vertical,
      left: left,
      right: !left
    }, className),
    ref: wrapperRef
  }, /*#__PURE__*/React.createElement("div", {
    className: tabs,
    role: "tablist",
    ref: tabsRef
  }, children), /*#__PURE__*/React.createElement(TabIndicator, {
    vertical: vertical,
    left: left,
    onTransitionEnd: checkAndUpdate,
    style: _objectSpread2({}, indicatorStyle)
  }))));
};
TabsComponent.propTypes = {
  ariaLabel: PropTypes.string,
  classes: PropTypes.objectOf(PropTypes.string),
  children: PropTypes.node,
  className: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /** Controls tabs layout direction (horizontal by default) */
  vertical: PropTypes.bool,

  /**
   * Controls side of active tab indicator for vertical layout
   * (will be rendered with "right: 0" by default)
   */
  left: PropTypes.bool
};
TabsComponent.defaultProps = {
  ariaLabel: undefined,
  classes: {
    tabs: undefined
  },
  children: undefined,
  className: undefined,
  value: 0,
  vertical: false,
  left: false
};
var StyledTabs = withStyles(styles)(TabsComponent);
StyledTabs.displayName = 'Tabs';

/* istanbul ignore file */
var styles$2 = (function (_ref) {
  var _ref$tabs = _ref.tabs,
      tab = _ref$tabs.tab,
      tabAddition = _ref$tabs.tabAddition,
      tabDark = _ref$tabs.tabDark;
  return {
    tab: _objectSpread2({
      background: 'none',
      padding: 0,
      color: function color(_ref2) {
        var dark = _ref2.dark;
        return dark ? tabDark.color : tab.color;
      },
      border: 'none',
      cursor: 'pointer',
      display: 'inline-block',
      '&:focus': {
        outlineColor: function outlineColor(_ref3) {
          var dark = _ref3.dark;
          return dark ? tabDark.darkFocusColor : tab.focusColor;
        }
      },
      '&:active': {
        outline: 'none'
      }
    }, tabAddition),
    selected: {
      color: function color(_ref4) {
        var dark = _ref4.dark;
        return dark ? tabDark.color : tab.selectedColor;
      }
    }
  };
});

var _excluded$1 = ["children", "classes", "className", "onChange", "value", "dark"];
function TabComponent(props) {
  var _classnames;

  var children = props.children,
      classes = props.classes,
      className = props.className,
      onChange = props.onChange,
      value = props.value,
      dark = props.dark,
      other = _objectWithoutProperties(props, _excluded$1);

  var context = useContext(TabsContext);
  var selected = value === context.value;
  useEffect(function () {
    if (!selected && props.selected) {
      context.setValue(value);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  var handleChange = function handleChange(event) {
    event.preventDefault();
    onChange(event, value);
  };

  return /*#__PURE__*/React.createElement("button", _extends({
    className: classnames('tab', classes.tab, (_classnames = {}, _defineProperty(_classnames, classes.selected, selected), _defineProperty(_classnames, "selected", selected), _classnames), className),
    role: "tab",
    "aria-selected": selected
  }, other, {
    onClick: handleChange,
    tabIndex: "0",
    type: "button"
  }), children);
}
TabComponent.propTypes = {
  children: PropTypes.node,
  classes: PropTypes.objectOf(PropTypes.string),
  className: PropTypes.string,
  onChange: PropTypes.func,
  selected: PropTypes.bool,
  dark: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
};
TabComponent.defaultProps = {
  children: undefined,
  classes: undefined,
  className: undefined,
  onChange: undefined,
  selected: false,
  dark: false
};
var StyledTab = withStyles(styles$2)(TabComponent);
StyledTab.displayName = 'Tab';

var styles$3 = function styles(_ref) {
  var palette = _ref.palette,
      _ref$tabs = _ref.tabs,
      tab = _ref$tabs.tab,
      styledTab = _ref$tabs.styledTab,
      badge = _ref$tabs.badge,
      defaultTabBorder = _ref$tabs.defaultTabBorder,
      defaultTabBorderColor = _ref$tabs.defaultTabBorderColor;
  return {
    tab: _objectSpread2(_objectSpread2({
      borderStyle: 'solid',
      borderColor: function borderColor(_ref2) {
        var tabColor = _ref2.tabColor;
        return "".concat(tabColor || defaultTabBorder, " \n    ").concat(defaultTabBorderColor, " ").concat(defaultTabBorderColor, " ").concat(defaultTabBorderColor);
      },
      borderRadius: [3, 3, 0, 0],
      padding: [10, 16, 5],
      verticalAlign: 'bottom',
      textAlign: 'left',
      cursor: 'pointer',
      display: 'inline-block',
      marginRight: '-1px'
    }, styledTab), {}, {
      '&:focus': {
        outlineColor: tab.focusColor
      }
    }),
    tabBorder: {
      borderWidth: function borderWidth(_ref3) {
        var tabColor = _ref3.tabColor;
        return tabColor ? '5px 1px 1px' : 1;
      }
    },
    selected: {
      backgroundColor: palette.background,
      borderBottom: 'none',
      boxShadow: '0px -4px 9px -8px rgba(115,115,115,1);',
      position: 'relative',
      top: -4
    },
    title: {
      display: 'block',
      fontWeight: 'bold',
      fontSize: 16
    },
    subtitle: {
      display: 'block',
      fontSize: 12
    },
    badge: _objectSpread2({
      textAlign: 'center',
      display: 'inline'
    }, badge),
    actionButton: {
      display: 'inline'
    },
    tabContent: {
      display: 'flex'
    },
    titleCol: {
      display: 'flex',
      justifyContent: 'center',
      alignContent: 'center',
      flexDirection: 'column',
      minHeight: 35
    },
    actionBadgeCol: {
      display: 'flex',
      justifyContent: 'center',
      alignContent: 'center',
      flexDirection: 'column',
      minHeight: 35,
      padding: [0, 0, 0, 16]
    }
  };
};

var useStyles = createUseStyles(styles$3);

var _excluded$2 = ["className", "onChange", "value", "dark", "title", "subtitle", "action", "badge", "color", "index", "displayZeroBadge"];

function isBadgeDisplayed(badge, displayZero) {
  return Boolean(badge || !Number.isNaN(+badge) && +badge !== 0 || displayZero && +badge === 0);
}

function StyledTab$1(props) {
  var _classnames;

  var className = props.className,
      onChange = props.onChange,
      value = props.value,
      dark = props.dark,
      title = props.title,
      subtitle = props.subtitle,
      action = props.action,
      badge = props.badge,
      color = props.color,
      index = props.index,
      displayZeroBadge = props.displayZeroBadge,
      other = _objectWithoutProperties(props, _excluded$2);

  var theme = useTheme();

  var getDefaultColorForTab = function getDefaultColorForTab() {
    return theme.palette.visualizations[index];
  };

  var context = useContext(TabsContext);
  var selected = value === context.value;
  var tabColor = color === false ? undefined // use no color is color is disabled
  : color !== undefined ? color // use color if given
  : getDefaultColorForTab(); // pull theme color based on items order in tabs

  var classes = useStyles({
    tabColor: tabColor
  });
  useEffect(function () {
    if (!selected && props.selected) {
      context.setValue(value);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  var handleClick = function handleClick(e) {
    e.preventDefault();
    onChange(e, value);
  };

  var handleKeydown = function handleKeydown(e) {
    if (e.key === 'Space' || e.key === 'Enter') {
      onChange(e, value);
    }
  };

  return /*#__PURE__*/React.createElement("span", _extends({
    className: classnames('tab', classes.tab, classes.tabBorder, (_classnames = {}, _defineProperty(_classnames, classes.selected, selected), _defineProperty(_classnames, "selected", selected), _classnames), className),
    role: "tab",
    "aria-selected": selected
  }, other, {
    onClick: handleClick,
    onKeyDown: handleKeydown,
    tabIndex: "0"
  }), /*#__PURE__*/React.createElement("span", {
    className: classes.tabContent
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.titleCol
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.title
  }, title), subtitle && /*#__PURE__*/React.createElement("span", {
    className: classes.subtitle
  }, subtitle)), isBadgeDisplayed(badge, displayZeroBadge) && /*#__PURE__*/React.createElement("span", {
    className: classes.actionBadgeCol
  }, /*#__PURE__*/React.createElement("span", {
    role: "status",
    "aria-label": badge,
    className: classes.badge
  }, badge)), action && /*#__PURE__*/React.createElement("span", {
    className: classes.actionBadgeCol
  }, action)));
}
StyledTab$1.propTypes = {
  displayZeroBadge: PropTypes.bool,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  action: PropTypes.node,
  badge: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  color: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  className: PropTypes.string,
  onChange: PropTypes.func,
  dark: PropTypes.bool,
  selected: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  index: PropTypes.number
};
StyledTab$1.defaultProps = {
  className: undefined,
  onChange: undefined,
  dark: false,
  selected: false,
  subtitle: undefined,
  action: undefined,
  badge: undefined,
  color: undefined,
  index: 0,
  displayZeroBadge: false
};

/* istanbul ignore file */

var styles$4 = function styles(_ref) {
  var defaultTabBorderColor = _ref.tabs.defaultTabBorderColor;
  return {
    tabs: {
      position: 'relative',
      whiteSpace: 'nowrap',
      overflowY: 'hidden',
      overflowX: 'auto'
    },
    wrapper: {
      overflowX: 'auto',
      overflowY: 'hidden',
      paddingTop: 9,
      display: 'flex'
    },
    leftOverflow: {
      background: 'linear-gradient(90deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 100%)',
      width: 40,
      height: 67,
      display: 'block',
      zIndex: 10,
      position: 'absolute',
      left: 0,
      top: 0
    },
    rightOverflow: {
      background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 100%)',
      width: 40,
      height: 67,
      zIndex: 10,
      display: 'block',
      position: 'absolute',
      right: 0,
      top: 0
    },
    startLine: {
      display: 'inline-block',
      backgroundColor: defaultTabBorderColor,
      width: 15,
      height: 1,
      position: 'relative',
      top: 55,
      '&::after': {
        content: '""',
        position: 'absolute',
        width: 1,
        height: 4,
        top: -3,
        right: -1,
        display: 'block',
        backgroundColor: defaultTabBorderColor
      }
    },
    endLine: {
      maxWidth: 128,
      display: 'inline-block',
      backgroundColor: defaultTabBorderColor,
      width: '100%',
      height: 1,
      position: 'relative',
      top: 55,
      '&::before': {
        content: '""',
        position: 'absolute',
        width: 1,
        height: 4,
        top: -4,
        left: 0,
        display: 'block',
        backgroundColor: defaultTabBorderColor
      }
    }
  };
};

var useStyles$1 = createUseStyles(styles$4);

/**
 * Detect if given dom element is overflowing
 * @param clientWidth
 * @param clientHeight
 * @param scrollWidth
 * @param scrollHeight
 * @returns {boolean}
 */

var getIsOverflown = function getIsOverflown(_ref) {
  var clientWidth = _ref.clientWidth,
      clientHeight = _ref.clientHeight,
      scrollWidth = _ref.scrollWidth,
      scrollHeight = _ref.scrollHeight;
  return scrollHeight > clientHeight || scrollWidth > clientWidth;
};
/**
 * Detect if dom element is scrolled to left edge
 * @param scrollLeft
 * @returns {boolean}
 */

var getLeftEdge = function getLeftEdge(_ref2) {
  var scrollLeft = _ref2.scrollLeft;
  return scrollLeft === 0;
};
/**
 * Detect if dom element is scrolled to right edge
 * @param scrollLeft
 * @param clientWidth
 * @param scrollWidth
 * @returns {boolean}
 */

var getRightEdge = function getRightEdge(_ref3) {
  var scrollLeft = _ref3.scrollLeft,
      clientWidth = _ref3.clientWidth,
      scrollWidth = _ref3.scrollWidth;
  return scrollLeft + clientWidth === scrollWidth;
};
/**
 * Gives overflow state of given dom element
 * @param element
 * @returns {{leftEdge: boolean, overflown: boolean, rightEdge: boolean}}
 */

var useOverflow = function useOverflow(element) {
  var _useState = useState({
    overflown: false,
    leftEdge: false,
    rightEdge: false
  }),
      _useState2 = _slicedToArray(_useState, 2),
      overflow = _useState2[0],
      setOverflow = _useState2[1];

  var handleOverflow = function handleOverflow() {
    setOverflow({
      leftEdge: getLeftEdge(element.current),
      overflown: getIsOverflown(element.current),
      rightEdge: getRightEdge(element.current)
    });
  };

  useEffect(function () {
    window.addEventListener('resize', handleOverflow);
    element === null || element === void 0 ? void 0 : element.current.addEventListener('scroll', handleOverflow);
    handleOverflow();
    return function () {
      return window.removeEventListener('resize', handleOverflow);
    };
  }, []);
  return overflow;
};

var StyledTabs$1 = function StyledTabs(_ref) {
  var ariaLabel = _ref.ariaLabel,
      children = _ref.children,
      className = _ref.className,
      propValue = _ref.value;
  var wrapperRef = useRef(null);

  var _useState = useState(propValue),
      _useState2 = _slicedToArray(_useState, 2),
      value = _useState2[0],
      setValue = _useState2[1];

  var overflow = useOverflow(wrapperRef);
  var getParsedChildren = useCallback(function () {
    return Children.toArray(children);
  }, [children]);
  var getSelectedIndex = useMemo(function () {
    return getParsedChildren().findIndex(function (child) {
      return value === child.props.value || child.props.selected;
    });
  }, [children, value]);
  var ChildArray = getParsedChildren();
  var selectedIndex = getSelectedIndex > 0 ? getSelectedIndex : 0;
  var classes = useStyles$1();
  useEffect(function () {
    if (propValue !== value && propValue !== 0) {
      setValue(propValue);
    }
  }, [propValue, value]);
  return /*#__PURE__*/React.createElement(TabsContext.Provider, {
    value: {
      value: value,
      setValue: setValue
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.tabs
  }, overflow.overflown && !overflow.leftEdge && /*#__PURE__*/React.createElement("span", {
    className: classes.leftOverflow
  }), /*#__PURE__*/React.createElement("div", {
    ref: wrapperRef,
    "aria-label": ariaLabel || locale.get('tabs'),
    role: "tablist",
    className: "".concat(classes.wrapper, " ").concat(className)
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.startLine
  }), ChildArray.map(function (child, index) {
    return /*#__PURE__*/cloneElement(child, {
      index: index,
      selected: selectedIndex === index
    });
  }), /*#__PURE__*/React.createElement("span", {
    className: classes.endLine
  })), overflow.overflown && !overflow.rightEdge && /*#__PURE__*/React.createElement("span", {
    className: classes.rightOverflow
  })));
};
StyledTabs$1.propTypes = {
  ariaLabel: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};
StyledTabs$1.defaultProps = {
  ariaLabel: undefined,
  children: undefined,
  className: undefined,
  value: 0
};

/* istanbul ignore file */

var styles$5 = function styles() {
  return {
    styledContainer: {
      marginTop: -1
    }
  };
};

var useStyles$2 = createUseStyles(styles$5);

var StyledContainer = function StyledContainer(_ref) {
  var children = _ref.children,
      className = _ref.className;
  var classes = useStyles$2();
  return /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.styledContainer, " ").concat(className)
  }, children);
};

StyledContainer.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.string]).isRequired,
  className: PropTypes.string
};
StyledContainer.defaultProps = {
  className: ''
};

/* istanbul ignore file */

/**
 * Should have all locale keys used in this project with translations matching the language-region
 * code of this file.
 */
var defaultStrings_en_US = {
  tabs: 'Tabs'
};

export { StyledTabs as Tabs, StyledTab as Tab, TabIndicator, StyledTab$1 as StyledTab, StyledTabs$1 as StyledTabs, StyledContainer, defaultStrings_en_US };
